import { Extension } from "@tiptap/core";

export interface LineSpacingOptions {
  types: string[];
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    lineSpacing: {
      setLineSpacing: (value: string) => ReturnType;
      unsetLineSpacing: () => ReturnType;
    };
  }
}

/**
 * Per-block line spacing, same shape as the official TextAlign extension
 * (addGlobalAttributes + addCommands, not a hand-rolled node-attrs mixin).
 * Renders as `style="line-height:…"`; `null` (unset) renders no attribute.
 * Like textAlign, it survives save/reload only via the QdaDocument.content
 * sidecar — the flat `paragraphs` projection drops it.
 */
export const LineSpacing = Extension.create<LineSpacingOptions>({
  name: "lineSpacing",

  addOptions() {
    return { types: [] };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          lineSpacing: {
            default: null as string | null,
            parseHTML: (element: HTMLElement) => element.style.lineHeight || null,
            renderHTML: (attributes: Record<string, any>) => {
              if (!attributes.lineSpacing) return {};
              return { style: `line-height: ${attributes.lineSpacing}` };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setLineSpacing:
        (value: string) =>
        ({ commands }) =>
          this.options.types
            .map((type) => commands.updateAttributes(type, { lineSpacing: value }))
            .every(Boolean),
      unsetLineSpacing:
        () =>
        ({ commands }) =>
          this.options.types.map((type) => commands.resetAttributes(type, "lineSpacing")).every(Boolean),
    };
  },
});
